import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon';
import type {
  DatesSetArg,
  EventClickArg,
  MoreLinkContentArg,
} from '@fullcalendar/core';
import { useTranslation } from 'react-i18next';
import { Button, H4, IconButton } from 'design-system-web';
import { config } from 'lane-shared/config';
import { FONT_AWESOME_LIGHT } from 'lane-shared/helpers/constants/icons';
import { FullCalendarEvent } from '../hooks/useReservableCalendarView';
import styles from './DayGridCalendar.scss';

export const VIEW_MAP = {
  month: 'dayGridMonth',
} as const;

export type DayGridView = keyof typeof VIEW_MAP;

type Props = {
  events: FullCalendarEvent[];
  channelTimeZone: string;
  view?: DayGridView;
  headerActions?: React.ReactNode;
  onDatesSet: (arg: DatesSetArg) => void;
  eventClick: (arg: EventClickArg) => void;
  onMoreLinkClick: (date: Date, anchorRect: DOMRect) => void;
};

const TRANSLATION_KEYS = {
  today: 'web.admin.channel.reservableManagement.calendar.button.today',
};

type MonthMoreLinkProps = {
  text: string;
  channelTimeZone: string;
  onMoreLinkClick: (date: Date, anchorRect: DOMRect) => void;
};

// Custom "+N more" content that opens OUR DayModal and never lets FullCalendar
// mount its native popover.
//
// Why a native, capture-phase DOM listener (not a React onClick):
// FullCalendar v6 renders its own DOM with **preact**, and the `.fc-more-link`
// anchor gets a preact click/keydown handler bound directly on it via
// `createAriaClickAttrs(this.handleClick)` (see @fullcalendar/core 6.1.13,
// MoreLinkContainer -> createAriaClickAttrs -> createAriaKeyboardAttrs). That
// `handleClick` is the ONLY code path that calls
// `this.setState({ isPopoverOpen: true })` and mounts `MorePopover`. Our
// `moreLinkContent` element is a **React** node bridged into that anchor through
// `@fullcalendar/react` (CustomRenderingComponent -> createPortal), so a React
// `onClick.stopPropagation()` on our inner element does NOT stop preact's own
// handler bound on the anchor itself — FC still flips the popover open and we
// get TWO modals.
//
// The reliable interception is at the native DOM level, in the **capture
// phase**. IMPORTANT: we attach on the anchor's *parent* element, not the
// anchor itself. Per the DOM dispatch spec, at the AT_TARGET phase capture and
// bubble listeners on the target node fire in *registration order* (the capture
// flag only orders listeners on ANCESTORS of the target). FullCalendar binds
// its anchor handler at mount — before our `useEffect` runs — so a capture
// listener on the anchor would lose the race whenever the event target IS the
// anchor (a click on the anchor's padding, or keyboard activation: FC gives the
// anchor `tabIndex=0`, so the anchor is the real focus target). Attaching on the
// anchor's parent makes the parent a strict ancestor of every such event, so
// our capture listener always fires first. We guard with `anchor.contains` so
// we only act on events belonging to this more-link, then
// `stopImmediatePropagation()` + `preventDefault()`: preact's `handleClick`
// never runs, `isPopoverOpen` never flips, and MorePopover is never constructed
// (no native popover -> no duplicate, and no getBoundingClientRect-on-null
// crash). We derive the day from the enclosing `.fc-daygrid-day[data-date]` cell
// (parsed in channelTimeZone) and open the DayModal anchored off the real
// `.fc-more-link` rect.
//
// Listeners are attached exactly once per link and removed on unmount (see the
// useEffect cleanup). The latest callback / timezone are read through refs so
// the capture listeners never need to be re-bound on re-render.
export function MonthMoreLink({
  text,
  channelTimeZone,
  onMoreLinkClick,
}: MonthMoreLinkProps) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const onMoreLinkClickRef = useRef(onMoreLinkClick);
  const channelTimeZoneRef = useRef(channelTimeZone);

  onMoreLinkClickRef.current = onMoreLinkClick;
  channelTimeZoneRef.current = channelTimeZone;

  useEffect(() => {
    const anchor = spanRef.current?.closest<HTMLElement>('.fc-more-link');
    const container = anchor?.parentElement;

    if (!anchor || !container) {
      return undefined;
    }

    const openModal = (ev: Event) => {
      // Only handle events that belong to THIS more-link anchor. The listener
      // sits on the parent, so events for sibling content pass through here too.
      if (!(ev.target instanceof Node) || !anchor.contains(ev.target)) {
        return;
      }

      // Capture phase on the ancestor + stopImmediatePropagation runs before
      // (and prevents) FullCalendar's own preact click/keydown handler on the
      // anchor, so its native popover is never constructed.
      ev.stopImmediatePropagation();
      ev.preventDefault();

      const isoDate = anchor
        .closest('.fc-daygrid-day')
        ?.getAttribute('data-date');

      if (isoDate) {
        onMoreLinkClickRef.current(
          DateTime.fromISO(isoDate, {
            zone: channelTimeZoneRef.current,
          }).toJSDate(),
          anchor.getBoundingClientRect()
        );
      }
    };

    const handleClick = (ev: MouseEvent) => openModal(ev);
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        openModal(ev);
      }
    };

    container.addEventListener('click', handleClick, true);
    container.addEventListener('keydown', handleKeyDown, true);

    return () => {
      container.removeEventListener('click', handleClick, true);
      container.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  return (
    <span
      ref={spanRef}
      className={styles.moreLink}
      data-test="month-calendar-more-link"
    >
      {text}
    </span>
  );
}

export const DayGridCalendar = forwardRef<FullCalendar, Props>(function (
  {
    events,
    channelTimeZone,
    view = 'month',
    headerActions,
    onDatesSet,
    eventClick,
    onMoreLinkClick,
  },
  ref
) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const calendarRef = useRef<FullCalendar | null>(null);

  const setRefs = (instance: FullCalendar | null) => {
    calendarRef.current = instance;

    if (typeof ref === 'function') {
      ref(instance);
    } else if (ref) {
      ref.current = instance;
    }
  };

  const getApi = () => calendarRef.current?.getApi();

  const handleDatesSet = (arg: DatesSetArg) => {
    setTitle(arg.view.title);
    onDatesSet(arg);
  };

  // `moreLinkContent` renders our own React element (MonthMoreLink) inside
  // FullCalendar's `.fc-more-link` anchor. That element attaches a native,
  // capture-phase click/keydown listener on the anchor so FullCalendar's own
  // (preact) handler never runs and its native popover is never constructed —
  // only our custom DayModal opens. See MonthMoreLink for the full rationale.
  const renderMoreLinkContent = (arg: MoreLinkContentArg) => (
    <MonthMoreLink
      text={arg.text}
      channelTimeZone={channelTimeZone}
      onMoreLinkClick={onMoreLinkClick}
    />
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.controller}>
        <Button
          type="button"
          variant="tertiary"
          size="large"
          onClick={() => getApi()?.today()}
          testId="month-calendar-today"
        >
          {t(TRANSLATION_KEYS.today)}
        </Button>
        <IconButton
          type={FONT_AWESOME_LIGHT}
          icon="angle-left"
          size="medium"
          onClick={() => getApi()?.prev()}
          data-test="month-calendar-prev"
        />
        <IconButton
          type={FONT_AWESOME_LIGHT}
          icon="angle-right"
          size="medium"
          onClick={() => getApi()?.next()}
          data-test="month-calendar-next"
        />
        <H4 data-test="month-calendar-title">{title}</H4>
        {headerActions && <div className={styles.actions}>{headerActions}</div>}
      </div>
      <div className={styles.calendar}>
        <FullCalendar
          ref={setRefs}
          schedulerLicenseKey={config.fullCalendar.licenseKey}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            luxonPlugin,
          ]}
          initialView={VIEW_MAP[view]}
          headerToolbar={false}
          dayMaxEvents
          moreLinkContent={renderMoreLinkContent}
          displayEventTime={false}
          firstDay={0}
          aspectRatio={1.35}
          expandRows
          timeZone={channelTimeZone}
          noEventsText="No events in this range."
          events={events}
          datesSet={handleDatesSet}
          eventClick={eventClick}
          eventDidMount={info => {
            info.el.setAttribute('data-test', 'month-calendar-event');
          }}
        />
      </div>
    </div>
  );
});
